import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
	ChevronDown,
	ChevronUp,
	FilePlus,
	FileText,
	Home,
	ListPlus,
	Plus,
	Building2,
	User2,
	Calendar,
	Code,
	ListTodoIcon,
	Presentation,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/hooks/use-sidebar";
import AddEmployeeModal from "@/components/employee/AddEmployeeModal";
import CreateMFOCodeModal from "@/components/mfo/CreateMFOCodeModal";
import AddDivisionModal from "@/components/division/AddDivisionModal";
import { useToast } from "@/hooks/use-toast";
import { createDepartment } from "@/utils/supabase/departmentUtils";

type NavItemProps = {
	to: string;
	icon: React.ReactNode;
	label: string;
	active?: boolean;
	onClick?: () => void;
};

const NavItem = ({ to, icon, label, active, onClick }: NavItemProps) => {
	if (onClick) {
		return (
			<button
				onClick={onClick}
				className={cn(
					"flex items-center space-x-3 py-2.5 px-4 rounded-md transition-all duration-300 group w-full text-left",
					active
						? "bg-secondary text-primary font-medium"
						: "text-foreground/70 hover:text-foreground hover:bg-secondary/50"
				)}>
				<span
					className={cn(
						"transition-colors",
						active
							? "text-primary"
							: "text-muted-foreground group-hover:text-foreground/90"
					)}>
					{icon}
				</span>
				<span>{label}</span>
			</button>
		);
	}

	return (
		<Link
			to={to}
			className={cn(
				"flex items-center space-x-3 py-2.5 px-4 rounded-md transition-all duration-300 group",
				active
					? "bg-secondary text-primary font-medium"
					: "text-foreground/70 hover:text-foreground hover:bg-secondary/50"
			)}>
			<span
				className={cn(
					"transition-colors",
					active
						? "text-primary"
						: "text-muted-foreground group-hover:text-foreground/90"
				)}>
				{icon}
			</span>
			<span>{label}</span>
		</Link>
	);
};

const Sidebar = () => {
	const location = useLocation();
	const { toast } = useToast();
	const { isOpen } = useSidebar();
	const [quickAddOpen, setQuickAddOpen] = useState(true);
	const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
	const [showCreateMFOCodeModal, setShowCreateMFOCodeModal] = useState(false);
	const [showAddDivisionModal, setShowAddDivisionModal] = useState(false);

	const handleAddEmployee = (employee: {
		name: string;
		email: string;
		position: string;
		phone: string;
		status: string;
		start_date: string;
		department_id?: string;
	}) => {
		toast({
			title: "Employee Added",
			description: `${employee.name} has been added successfully.`,
		});
	};

	const handleCreateMFOCode = (data: {
		code: string;
		title: string;
		budget: number;
		collaborators: import("@/types/kanban").TaskAssignee[];
	}) => {
		toast({
			title: "MFO Code Created",
			description: `Successfully created MFO Code: ${data.code}`,
		});
	};

	const handleAddDivision = async (division: {
		name: string;
		description: string;
		headName: string;
		headTitle: string;
		budget: number;
		vision: string;
		mission: string;
		goals: string[];
	}) => {
		try {
			const newDepartment = await createDepartment(division);

			if (newDepartment) {
				toast({
					title: "Division Added",
					description: `${division.name} has been added successfully.`,
				});
			} else {
				toast({
					title: "Error",
					description: "Failed to add division to the database",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Error adding division:", error);
			toast({
				title: "Error",
				description: "An unexpected error occurred while adding the division",
				variant: "destructive",
			});
		} finally {
			setShowAddDivisionModal(false);
		}
	};

	if (!isOpen) return null;

	return (
		<aside className='w-64 border-r border-border h-screen flex flex-col bg-sidebar transition-all duration-300 fixed overflow-y-auto z-50'>
			<div className='p-4 flex-1 flex flex-col space-y-1'>
				<Link
					to='/dashboard'
					className='flex items-center gap-2 my-3'>
					<img
						src='/logo.png'
						alt='Logo'
						className='h-8 w-8'
					/>
					<span className='font-semibold hidden md:inline-block'>
						MFO Management System
					</span>
				</Link>
				<NavItem
					to='/dashboard'
					icon={<Home size={20} />}
					label='Overview'
					active={location.pathname === "/dashboard"}
				/>
				<NavItem
					to='/dashboard/divisions'
					icon={<Building2 size={20} />}
					label='Divisions'
					active={location.pathname.startsWith("/dashboard/divisions")}
				/>
				<NavItem
					to='/dashboard/employees'
					icon={<User2 size={20} />}
					label='Employees'
					active={location.pathname.startsWith("/dashboard/employees")}
				/>
				<NavItem
					to='/dashboard/mfo'
					icon={<FileText size={20} />}
					label='MFOs'
					active={location.pathname === "/dashboard/mfo"}
				/>
				<NavItem
					to='/dashboard/mfo-codes'
					icon={<Code size={20} />}
					label='MFO Codes'
					active={location.pathname === "/dashboard/mfo-codes"}
				/>
				<NavItem
					to='/dashboard/sub-tasks'
					icon={<ListTodoIcon size={20} />}
					label='MFO Sub Tasks'
					active={location.pathname === "/dashboard/sub-tasks"}
				/>
				<NavItem
					to='/dashboard/kanban'
					icon={<ListPlus size={20} />}
					label='MFO Management'
					active={location.pathname === "/dashboard/kanban"}
				/>
				<NavItem
					to='/dashboard/calendar'
					icon={<Calendar size={20} />}
					label='Calendar'
					active={location.pathname === "/dashboard/calendar"}
				/>
				<NavItem
					to='/dashboard/mfo-presentation'
					icon={<Presentation size={20} />}
					label='MFO Presentation'
					active={location.pathname === "/dashboard/mfo-presentation"}
				/>

				<div className='pt-6'>
					<Button
						variant='ghost'
						className={cn(
							"w-full justify-between items-center py-2.5 px-4 text-left rounded-md transition-all",
							quickAddOpen ? "bg-secondary" : ""
						)}
						onClick={() => setQuickAddOpen(!quickAddOpen)}>
						<div className='flex items-center space-x-3'>
							<Plus
								size={20}
								className={cn("text-muted-foreground", quickAddOpen && "text-primary")}
							/>
							<span className={cn(quickAddOpen && "font-medium text-primary")}>
								Quick Add
							</span>
						</div>
						{quickAddOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					</Button>

					{quickAddOpen && (
						<div className='ml-6 space-y-1 mt-1 animate-slide-up'>
							<NavItem
								to='#'
								icon={<Building2 size={18} />}
								label='Add Division'
								onClick={() => setShowAddDivisionModal(true)}
							/>
							<NavItem
								to='#'
								icon={<FilePlus size={18} />}
								label='Create MFO'
								onClick={() => setShowCreateMFOCodeModal(true)}
							/>
							<NavItem
								to='#'
								icon={<User2 size={18} />}
								label='Add Employee'
								onClick={() => setShowAddEmployeeModal(true)}
							/>
						</div>
					)}
				</div>
			</div>

			<div className='p-4 border-t border-border'>
				<div className='text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider'>
					CTCAD
				</div>
				<div className='text-xs text-muted-foreground'>
					Made with ❤️ by Francis Tin-ao (CSU BSIT Intern)
				</div>
			</div>

			<AddEmployeeModal
				isOpen={showAddEmployeeModal}
				onOpenChange={setShowAddEmployeeModal}
				onSave={handleAddEmployee}
			/>
			<CreateMFOCodeModal
				isOpen={showCreateMFOCodeModal}
				onOpenChange={setShowCreateMFOCodeModal}
				onSave={handleCreateMFOCode}
				mode='create'
			/>
			<AddDivisionModal
				isOpen={showAddDivisionModal}
				onOpenChange={setShowAddDivisionModal}
				onSave={handleAddDivision}
			/>
		</aside>
	);
};

export default Sidebar;
